import { Link } from '@tanstack/react-router';
import { Diamond } from '@/components/ui';
import { cn } from '@/lib/utils';
import { company } from '@/lib/company';
import { env } from '@/lib/env';
import { openConsentSettings } from '@/lib/consent';
import { m } from '@/paraglide/messages';

export function Footer() {
  return (
    <footer
      className={cn(
        'bg-[var(--ollio-navy)] text-white/80 font-sans',
        'px-6 py-10 md:px-10 md:pt-14 md:pb-10',
        'mt-20',
      )}
    >
      {/* ── Top region ────────────────────────────────────────────────── */}
      <div className="max-w-[1100px] mx-auto flex flex-wrap gap-12 md:gap-16">

        {/* Brand block */}
        <div className="max-w-[280px]">
          {/* Gold brand lockup (gold-on-transparent → reads on the navy footer). */}
          <img src="/ollio-lockup-gold.png" alt="Ollio Apartments" className="h-20 w-auto" />
          <p className="mt-4 text-sm leading-relaxed text-white/70">
            {m.footer_tagline()}
          </p>
        </div>

        {/* Links block */}
        <div>
          <p className="text-[var(--ollio-gold)] text-xs font-bold uppercase tracking-[0.12em] mb-3.5">
            Support
          </p>
          <nav
            aria-label="Footer links"
            className="flex flex-col gap-2.5"
          >
            <Link
              to="/legal/$slug"
              params={{ slug: 'terms' }}
              className="text-sm text-white/75 hover:text-white transition"
            >
              Terms
            </Link>
            <Link
              to="/legal/$slug"
              params={{ slug: 'privacy' }}
              className="text-sm text-white/75 hover:text-white transition"
            >
              Privacy
            </Link>
            <Link
              to="/legal/$slug"
              params={{ slug: 'cancellation' }}
              className="text-sm text-white/75 hover:text-white transition"
            >
              Cancellation Policy
            </Link>
            <Link
              to="/help"
              className="text-sm text-white/75 hover:text-white transition"
            >
              Help / FAQ
            </Link>
            {/* The permanent way back to the consent choice. Without it the
                banner is a one-time question, and a consent that cannot be
                withdrawn as easily as it was given is not valid consent
                (GDPR Art. 7(3)). A <button>, not a link — it opens a panel,
                it does not navigate. */}
            <button
              type="button"
              onClick={openConsentSettings}
              data-testid="footer-cookie-settings"
              className="text-left text-sm text-white/75 hover:text-white transition"
            >
              {m.footer_cookie_settings()}
            </button>
          </nav>
        </div>

        {/* Contact block.
            Before OH-91 the only way to reach us from the site was the FAQ
            page or the checkout — so a guest deciding whether to trust us
            with money on the home page had no address to write to and no
            sign that anyone answers. The reply time is here rather than in
            the FAQ alone because an email address with no stated turnaround
            reads as a form that goes nowhere.

            No phone: it stays on the FAQ and the booking page, where it
            answers "I am mid-stay and something is wrong", rather than in
            the footer, which would read as an invitation to call. Owner's
            call, 2026-08-25. */}
        <div>
          <p className="text-[var(--ollio-gold)] text-xs font-bold uppercase tracking-[0.12em] mb-3.5">
            {m.footer_contact_heading()}
          </p>
          <a
            href={`mailto:${company.bookingsEmail}`}
            className="text-sm text-white/75 hover:text-white transition"
          >
            {company.bookingsEmail}
          </a>
          <p className="mt-2 max-w-[220px] text-sm leading-relaxed text-white/55">
            {m.footer_response_time()}
          </p>
        </div>
      </div>

      {/* ── Bottom bar ───────────────────────────────────────────────── */}
      {/* Imprint — who the guest is actually contracting with, on every page
          rather than only at the point of payment. The checkout carries the
          same identity because Consumer Rights Directive Art 8(2) requires it
          directly above the order button; this is the general disclosure of
          Art 6(1)(b)-(c), which is owed BEFORE the guest is bound, and the
          checkout is far too late to be the first sight of it.

          Values come from lib/company.ts — the same module the checkout and
          the booking page read, so there is one place to change them. The
          three legal documents state the same identity in their own text and
          are deliberately NOT templated from here: the consent ledger records
          a document by the sha256 of its content, so a page that substituted
          values at render time would show the guest something other than what
          we recorded them agreeing to. */}
      <address
        data-testid="footer-imprint"
        className={cn(
          'not-italic',
          'border-t border-white/[0.14] pt-5 mt-10',
          'max-w-[1100px] mx-auto',
          'text-[13px] leading-relaxed text-white/55',
        )}
      >
        <span className="text-white/70">{company.name}</span> · {company.address}
        <br />
        {m.company_registration_label()} {company.registrationNumber} ·{' '}
        {m.company_tax_label()} {company.taxNumber} · {m.company_complaints_label()}:{' '}
        <a
          href={`mailto:${company.legalEmail}`}
          className="underline underline-offset-2 hover:text-white/80"
        >
          {company.legalEmail}
        </a>
      </address>

      <div
        className={cn(
          'pt-4 mt-4',
          'max-w-[1100px] mx-auto',
          'flex flex-wrap justify-between gap-3',
          'text-[13px] text-white/55',
        )}
      >
        <span>
          © {new Date().getFullYear()} {m.brand_name()} · Belgrade
        </span>
        <span className="flex items-center gap-2">
          Direct <Diamond /> Booking.com <Diamond /> Airbnb
        </span>
      </div>

      {/* ── Build-sha marker ─────────────────────────────────────────── */}
      {/* The deploy smoke test greps [data-build-sha] from the static
          index.html; keep this visible marker in sync so curl/grep works. */}
      <p
        className="max-w-[1100px] mx-auto mt-2 font-mono text-xs text-white/40"
        data-build-sha={env.buildSha}
      >
        build {env.buildSha}
      </p>
    </footer>
  );
}
